/**
 * S8 — Diagnostics, at `/about`.
 *
 * Its real job is a phone call: *"which environment am I in?"* asked by a manager
 * standing in a walk-in while Copeland is on the other end. So the answers that
 * matter — BC environment, the identity the backend authenticates as, auth mode,
 * whether writes are even allowed — are rendered plainly and without
 * interpretation, and the config **filenames** are shown too, because "which
 * thresholds am I running" has a right answer that a filename settles.
 *
 * Pending-sync count and "flush now" appear here per § 3, but the offline queue
 * is D4. Rather than a fake zero, this screen says the queue does not exist yet —
 * a diagnostics screen that lies about sync state is worse than one that admits
 * its own scope.
 */
import { useHealth } from "../api/queries";
import { useAuth } from "../auth/AuthContext";
import { DEV_SHIM_COMPILED_IN } from "../auth/devShim";

const BUILD_MODE = import.meta.env.MODE;

export function Diagnostics() {
  const { data: health, isLoading, error, refetch, isFetching } = useHealth();
  const { authMode, devUpn, devShimActive, signOut, config, upn, entraReady } = useAuth();

  return (
    <section>
      <h2>Diagnostics</h2>

      <div className="card">
        <h3>This app</h3>
        <dl className="facts">
          <div>
            <dt>Bundle</dt>
            <dd>
              {BUILD_MODE}
              {DEV_SHIM_COMPILED_IN ? " · dev auth shim compiled in" : " · no dev shim"}
            </dd>
          </div>
          <div>
            <dt>Signed in as</dt>
            {/* The UPN the **backend** will authorise on, lower-cased, whichever
                path produced it — not a display name. "Which account am I?" is
                asked on this screen precisely when the answer matters, and a
                friendly name would not settle it. */}
            <dd>{upn ?? devUpn ?? <span className="muted">nobody</span>}</dd>
          </div>
          {authMode === "entra" && (
            <div>
              <dt>Sign-in</dt>
              <dd>
                {entraReady ? "MSAL ready" : "MSAL not initialised"}
                {config?.client_id ? ` · app ${config.client_id}` : " · no client id"}
              </dd>
            </div>
          )}
          <div>
            <dt>Auth mode (server)</dt>
            <dd>{authMode ?? <span className="muted">unknown</span>}</dd>
          </div>
          <div>
            <dt>Allowed UPN domain</dt>
            <dd>{config?.allowed_upn_domain ?? <span className="muted">—</span>}</dd>
          </div>
          <div>
            <dt>Pending sync</dt>
            <dd className="muted">
              No offline queue yet — entry is D3 and the queue is D4. Nothing is
              held on this device.
            </dd>
          </div>
        </dl>
        {devShimActive && (
          <button type="button" onClick={signOut}>
            Sign out
          </button>
        )}
      </div>

      <div className="card">
        <h3>
          Backend <code>/health</code>
        </h3>
        {isLoading && <p className="muted">Reading health…</p>}
        {error && <p className="error-text">{(error as Error).message}</p>}
        {health && (
          <>
            <dl className="facts">
              <div>
                <dt>BC environment</dt>
                {/* The question this screen exists for. */}
                <dd>
                  <strong>{health.bc_environment}</strong>
                </dd>
              </div>
              <div>
                <dt>BC identity</dt>
                <dd>{health.bc_identity}</dd>
              </div>
              <div>
                <dt>BC writes allowed</dt>
                <dd>
                  {health.bc_writes_allowed ? "yes" : "no — reads only in this environment"}
                </dd>
              </div>
              <div>
                <dt>BC credentials</dt>
                <dd>{health.bc_credentials_configured ? "configured" : "missing"}</dd>
              </div>
              <div>
                <dt>Backend version</dt>
                <dd>{health.version}</dd>
              </div>
              <div>
                <dt>Count areas</dt>
                <dd>{health.count_areas}</dd>
              </div>
              <div>
                <dt>Access config</dt>
                <dd>
                  <code>{health.access_config}</code>
                </dd>
              </div>
              <div>
                <dt>Swing thresholds</dt>
                <dd>
                  <code>{health.swing_thresholds}</code>
                </dd>
              </div>
            </dl>
          </>
        )}
        <button type="button" onClick={() => void refetch()} disabled={isFetching}>
          {isFetching ? "Checking…" : "Check again"}
        </button>
      </div>
    </section>
  );
}
